/**
 * «Responder a un desafío»: the holder's half of strong mode. The holder scans
 * the challenge the verifier's phone shows and answers it with a mode-1
 * presentation carrying the verifier's nonce, and the errand chosen on the
 * card when there is one.
 *
 * One answer per challenge, so no ten-second regeneration: the verifier spends
 * the challenge on the first answer it accepts, and its two minutes, not the
 * holder's timestamp, are what make the answer fresh. The timestamp is still
 * the corrected clock's, because it is signed and should be true.
 */
import { ATTR, MODE_STRONG } from "../constants";
import { decodeAttributes, decodeChallengeQr, decodeCredential, decodeErrand } from "../format";
import { buildPresentation, chooseNonce } from "../present";
import { emitQR } from "../qr";
import { startScanner } from "../scan";
import { correctedNow, loadCredential, loadDeviceKey } from "../storage";
import { credentialPhase, expiresAtOf } from "../sync";
import { escapeHTML } from "../ui";
import { scanOnce } from "./camera";
import type { Scanner } from "./scan";
import { ICON } from "../icons";

const NOT_A_CHALLENGE =
  "Ese código no es un desafío. Apunta al que muestra el otro teléfono después de tocar «Modo reforzado».";

/** Whose errand rides in the answer, for the line under the code; empty when
 *  the bytes do not decode, which the card would not have offered. */
function grantorOf(errandBytes: Uint8Array): string {
  try {
    const credential = decodeCredential(decodeErrand(errandBytes).credentialBytes);
    return String(decodeAttributes(credential.attributesBytes).get(ATTR.NAME) ?? "");
  } catch {
    return "";
  }
}

export async function renderChallengeAnswer(
  root: HTMLElement,
  onDone: () => void,
  errandBytes?: Uint8Array,
  scanner: Scanner = startScanner,
): Promise<() => void> {
  const credentialBytes = await loadCredential();
  const pair = await loadDeviceKey();
  if (!credentialBytes || !pair) throw new Error("no credential on this device");
  let stopCamera: () => void = () => {};

  const stopWith = (sentence: string) => {
    root.innerHTML = `<div class="problem"><div class="badge">${ICON.cross}</div><h1>Sin respuesta</h1>
      <p>${escapeHTML(sentence)}</p>
      <div class="foot"><button id="done">Volver a mi identidad</button></div></div>`;
    root.querySelector("#done")!.addEventListener("click", onDone);
  };

  const scan = async () => {
    stopCamera = await scanOnce(
      root, "Escanea el desafío que te muestra quien verifica",
      (payload) => void answer(decodeChallengeQr(payload)), scanner,
      (payload) => {
        try {
          decodeChallengeQr(payload);
          return null;
        } catch {
          return NOT_A_CHALLENGE;
        }
      },
    );
  };

  const answer = async (nonce: Uint8Array) => {
    // The card's two refusals, for the card's reasons: a code from a phone
    // that never learned the server's time, or from an expired credential, is
    // one every verifier refuses, and saying so here tells the holder why.
    const { now, synced } = await correctedNow();
    if (!synced) {
      stopWith("Este teléfono nunca ha puesto en hora la aplicación. Conéctate a internet una vez y vuelve a abrirla.");
      return;
    }
    if (credentialPhase(expiresAtOf(credentialBytes), now, null).phase !== "valid") {
      stopWith("Tu identidad caducó. Conéctate a la red para renovarla.");
      return;
    }
    const payload = await buildPresentation({
      credentialBytes,
      deviceKey: pair.privateKey,
      nonce: chooseNonce(MODE_STRONG, nonce),
      mode: MODE_STRONG,
      timestamp: now,
      errandBytes,
    });
    const grantor = errandBytes ? grantorOf(errandBytes) : "";
    root.innerHTML = `<div class="problem qr-page"><h1>Respuesta al desafío</h1>
      <p>Muestra este código a quien te verifica. Sirve solo para su desafío.</p>
      ${errandBytes ? `<p>Lleva el recado de ${escapeHTML(grantor)}.</p>` : ""}
      <div class="qr" id="answer-qr">${(await emitQR(payload))[0]}</div>
      <div class="foot"><button id="again" class="ghost">Responder a otro desafío</button>
      <button id="done">Volver a mi identidad</button></div></div>`;
    root.querySelector("#again")!.addEventListener("click", () => void scan());
    root.querySelector("#done")!.addEventListener("click", onDone);
  };

  await scan();
  return () => stopCamera();
}
